import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Sparkles, ArrowRight, Calendar, Mail } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function ConciergeSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ phase: "checking", attempts: 0, data: null });

    useEffect(() => {
        if (!sessionId) {
            setState({ phase: "error", message: "Missing session_id", attempts: 0 });
            return;
        }
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 10;

        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/concierge/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState({ phase: "success", data, attempts });
                    return;
                }
                if (data.status === "expired") {
                    setState({ phase: "expired", data, attempts });
                    return;
                }
                attempts += 1;
                if (attempts >= maxAttempts) {
                    setState({ phase: "timeout", data, attempts });
                    return;
                }
                setState({ phase: "pending", data, attempts });
                setTimeout(poll, 2000);
            } catch (e) {
                setState({ phase: "error", message: e.response?.data?.detail || e.message, attempts });
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    const renderContent = () => {
        if (state.phase === "checking" || state.phase === "pending") {
            return (
                <>
                    <Loader2 className="h-12 w-12 mx-auto text-coral animate-spin mb-4" />
                    <h1 className="font-display text-3xl font-bold mb-2 text-foreground">Confirming your payment…</h1>
                    <p className="text-muted-foreground font-app">
                        Polling Stripe for confirmation. This usually takes a few seconds. (Attempt {state.attempts + 1}/10)
                    </p>
                </>
            );
        }
        if (state.phase === "success") {
            const d = state.data;
            return (
                <>
                    <div className="h-16 w-16 rounded-full bg-fiji-green/15 grid place-items-center mx-auto mb-4">
                        <CheckCircle2 className="h-10 w-10 text-fiji-green" />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] font-app text-coral font-bold mb-2">
                        Bula Concierge · {d.tier_label}
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold mb-3 text-foreground" data-testid="concierge-success-heading">
                        Vinaka — your tier is active.
                    </h1>
                    <p className="text-muted-foreground font-app mb-8 max-w-md mx-auto">
                        We've received your payment for the <strong className="text-foreground">{d.tier_label}</strong> tier
                        ({" "}USD ${Number(d.amount_usd).toLocaleString()}). A welcome packet is on its way to your inbox.
                    </p>

                    <Card className="p-5 mb-5 bg-coral/5 dark:bg-coral/10 border-coral/30 text-left" data-testid="concierge-success-next-steps">
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-lg bg-coral/15 grid place-items-center shrink-0">
                                <Calendar className="h-4 w-4 text-coral" />
                            </div>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider font-app text-coral font-bold mb-1">
                                    Step 1 · Book your kickoff call
                                </div>
                                <div className="font-display text-base font-bold text-foreground mb-2">
                                    Pick a time that works for you
                                </div>
                                {d.calendly_url ? (
                                    <Button asChild className="bg-coral hover:bg-coral-deep text-white" size="sm">
                                        <a href={d.calendly_url} target="_blank" rel="noopener noreferrer" data-testid="concierge-calendly-cta">
                                            <Calendar className="h-3.5 w-3.5 mr-1.5" /> Book onboarding call
                                        </a>
                                    </Button>
                                ) : (
                                    <p className="text-sm font-app text-muted-foreground">Booking link arriving in your welcome email.</p>
                                )}
                            </div>
                        </div>
                    </Card>

                    <Card className="p-5 mb-6 bg-card border-border text-left">
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-lg bg-fiji-green/15 grid place-items-center shrink-0">
                                <Mail className="h-4 w-4 text-fiji-green" />
                            </div>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider font-app text-fiji-green font-bold mb-1">
                                    Step 2 · Watch your inbox
                                </div>
                                <div className="font-display text-base font-bold text-foreground mb-1">
                                    Welcome packet & coordinator intro
                                </div>
                                <p className="text-sm font-app text-muted-foreground">
                                    Within one business day, your dedicated coordinator will email you a personalized
                                    roadmap and document checklist.
                                    {!d.welcome_email_sent && (
                                        <span className="block mt-1 text-xs italic">(If you don't see it, check your spam folder.)</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </Card>

                    <div className="flex flex-wrap gap-3 justify-center">
                        <Button asChild className="bg-ocean-green text-white">
                            <Link to="/dashboard">Go to dashboard <ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                        <Button asChild variant="outline">
                            <Link to="/marketplace">Browse marketplace</Link>
                        </Button>
                    </div>
                </>
            );
        }
        if (state.phase === "expired" || state.phase === "timeout") {
            return (
                <>
                    <XCircle className="h-12 w-12 mx-auto text-coral mb-4" />
                    <h1 className="font-display text-3xl font-bold mb-3 text-foreground">Payment not confirmed</h1>
                    <p className="text-muted-foreground font-app mb-6">
                        {state.phase === "expired"
                            ? "The checkout session expired before payment completed."
                            : "We couldn't confirm payment status in time. If you completed checkout, refresh this page in a moment — or check your email for the receipt."}
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Button asChild className="bg-coral hover:bg-coral-deep text-white">
                            <Link to="/concierge">Back to tiers</Link>
                        </Button>
                        <Button asChild variant="outline">
                            <Link to="/">Home</Link>
                        </Button>
                    </div>
                </>
            );
        }
        return (
            <>
                <XCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
                <h1 className="font-display text-3xl font-bold mb-3 text-foreground">Something went wrong</h1>
                <p className="text-muted-foreground font-app mb-6">{state.message || "Unknown error."}</p>
                <Button asChild className="bg-ocean-green text-white">
                    <Link to="/concierge">Back to tiers</Link>
                </Button>
            </>
        );
    };

    return (
        <div data-testid="concierge-success-page" className="max-w-2xl mx-auto px-4 py-20">
            <Card className="p-10 text-center bg-card">
                <Sparkles className="h-5 w-5 mx-auto text-coral mb-2" />
                {renderContent()}
            </Card>
        </div>
    );
}
